import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback, selectAtom } from 'jotai/utils';
import { StoreInfo } from 'services/api/store';
import lastUsedConversation from 'utils/lastUsedConversation';
import { lastUsedStoreIdCache, lastUsedExternalStoreIdCache } from 'utils/lastUsedStore';
import { isSbsMode } from 'hooks/business/useIsSbs';
import {
  conversationMapState,
  conversationAtomFamily,
  useSetConversation,
  useDeleteConversation,
  useGetConversationById,
  useBatchUpdateConversations,
  conversationSummaryMapState,
} from './lruConversation';
import { orgConfigState } from './useOrgConfig';
import { importantTodoState } from 'views/Conversation/ConvList/FilterTodo';
import { useCallback } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useGetMessageById } from './message';
import { batchGetTalk } from 'services/batchRequest/batchGetTalk';
import { getTalkInfoListWithPhone, getTalkInfoWithPhone } from 'services/api/conversation.convList';
import { uniq } from 'lodash-es';
import { logger } from 'services/logger';
import { conversationCategoryState } from './conversationsHooks/useConversationCategory';
import {
  categoryAwareSortFamily,
  categoryFilterStateFamily,
} from './conversationsHooks/useCategoryFilterState';

type Conversation = Conversation.Conversation;
type Message = Conversation.Message;

// #region 接待中的单个会话id

/** 接待中的单个会话id */
const currentConversationIdAtom = atom<Conversation['conversationId'] | null>(null);
export const currentConversationIdState = atom(
  (get) => get(currentConversationIdAtom),
  (_, set, next: Conversation['conversationId'] | null) => {
    set(currentConversationIdAtom, next);
    // 当会话 ID 更新时，更新最近使用的会话历史记录
    if (next !== null) {
      lastUsedConversation.push(next);
    }
  },
);

/** 外部获取`接待中的单个会话id` */
export const useCurrentConversationIdValue = () => {
  return useAtomValue(currentConversationIdState);
};

/** 外部设置`接待中的单个会话id` */
export const useSetCurrentConversationIdValue = () => {
  return useSetAtom(currentConversationIdState);
};
// #endregion

const conversationReadonlyState = atom(false);
export const useConversationReadonlyValue = () => useAtomValue(conversationReadonlyState);
export const useSetConversationReadonly = () => useSetAtom(conversationReadonlyState);

// #region 当前应用已获取的全部会话信息

// conversationMapState 已从 lruConversation.ts 导入

export const conversationIdListState = atom<Conversation['conversationId'][]>([]);

export const useConversationIdListValue = () => {
  return useAtomValue(conversationIdListState);
};

export const useSetConversationIdListValue = () => {
  return useSetAtom(conversationIdListState);
};

/** 当前已加载的会话列表是否有未读会话 */
export const hasUnreadConversation = atom((get) => {
  const conversationSummaryMap = get(conversationSummaryMapState);
  const conversationSummaryList = Array.from(conversationSummaryMap.values());
  return conversationSummaryList.some(
    ({ unreadCount, controlStatus }) => controlStatus && unreadCount,
  );
});
/** 外部获取当前已加载的会话列表是否有未读会话 */
export const useHasUnreadConversation = () => {
  return useAtomValue(hasUnreadConversation);
};

/** 同步远端未回复人数的时间点 */
export const unreplyCustormerTimeState = atom(0);
/** 更新`同步远端未回复人数的时间点` */
export const useSetUnreplyCustormerTimeValue = () => {
  return useSetAtom(unreplyCustormerTimeState);
};

/** `当前应用已获取的全部会话信息`的未回复人数总和 */
export const conversationUnreplyCustomer = atom(0);
/** 外部获取`当前应用已获取的全部会话信息`的未回复人数总和 */
export const useConversationUnreplyCustomer = () => {
  return useAtomValue(conversationUnreplyCustomer);
};
/** 外部获取`当前应用已获取的全部会话信息`的未回复人数总和 */
export const useSetConversationUnreplyCustomer = () => {
  return useSetAtom(conversationUnreplyCustomer);
};

/**
 * 客服从退货退款/取消订单创建的会话，如果没有发送成功的消息之前，在shopee平台那边都不存在。
 */
export const fakeConversationOrderSnMap: Partial<Record<Conversation.ConversationId, string>> = {};
export const addFakeConversationOrderSn = (
  conversationId: Conversation.ConversationId,
  orderSn: string,
) => (fakeConversationOrderSnMap[conversationId] = orderSn);
export const removeFakeConversationOrderSn = (conversationId: Conversation.ConversationId) =>
  delete fakeConversationOrderSnMap[conversationId];
export const getFakeConversationOrderSn = (conversationId: Conversation.ConversationId) =>
  fakeConversationOrderSnMap[conversationId];

// 是否会话从远到近模式
const isConversationDescModeState = atom((get) => {
  const conversationCategory = get(conversationCategoryState);
  const categoryFilterState = get(categoryFilterStateFamily(conversationCategory));
  const statusFilters = categoryFilterState?.filterAdvance?.statusFilters;
  const categoryAwareSort = get(categoryAwareSortFamily(conversationCategory));
  return (
    (statusFilters?.includes('UNPROCESSED') || statusFilters?.includes('IN_RECEPTION')) &&
    categoryAwareSort !== 'asc'
  );
});

export const useIsConversationDescMode = () => useAtomValue(isConversationDescModeState);

/**
 * 排序配置 atom - 只包含影响排序的状态
 * 当这些状态变化时才需要重新排序
 */
const sortConfigState = atom((get) => ({
  isDescMode: get(isConversationDescModeState) ?? false,
  isSbs: get(isSbsMode),
}));

/**
 * 排序比较函数 - 根据配置返回对应的比较器
 * 将比较函数提取出来避免每次排序时重新创建
 * 支持 Conversation 和 ConversationSummary 两种类型
 */
interface SortableConversation {
  conversationId: string;
  lastMsgTime: string;
  remindMark?: boolean;
  controlStatus?: boolean;
  supervisePower?: boolean;
  sbsIndex?: number;
}

const getSortComparator = (isDescMode: boolean, isSbs: boolean) => {
  if (isSbs) {
    return isDescMode
      ? (a: SortableConversation, b: SortableConversation) => b.sbsIndex! - a.sbsIndex!
      : (a: SortableConversation, b: SortableConversation) => a.sbsIndex! - b.sbsIndex!;
  }

  return (a: SortableConversation, b: SortableConversation) => {
    const isARemind = a.remindMark && (a.controlStatus || a.supervisePower);
    const isBRemind = b.remindMark && (b.controlStatus || b.supervisePower);

    if (isBRemind && !isARemind) return 1;
    if (!isBRemind && isARemind) return -1;

    if (isDescMode) {
      if (a.lastMsgTime === b.lastMsgTime) {
        return Number(a.conversationId) - Number(b.conversationId);
      }
      return parseInt(a.lastMsgTime, 10) - parseInt(b.lastMsgTime, 10);
    }

    if (a.lastMsgTime === b.lastMsgTime) {
      return Number(b.conversationId) - Number(a.conversationId);
    }
    return parseInt(b.lastMsgTime, 10) - parseInt(a.lastMsgTime, 10);
  };
};

/** 已排序的`当前应用已获取的全部会话信息`数组, 按最新消息降序排列 */
export const conversationListState = atom((get) => {
  const { isDescMode, isSbs } = get(sortConfigState);
  const conversationMap = get(conversationMapState);
  const convList = Array.from(conversationMap.values());

  const comparator = getSortComparator(isDescMode, isSbs);
  return convList.sort(comparator);
});
/** 外部获取已排序的`当前应用已获取的全部会话信息`数组 */
export const useConversationList = () => {
  return useAtomValue(conversationListState);
};

/**
 * 基于 summaryMap 的已排序会话摘要列表
 * 用于列表渲染，不受 LRU 回收影响
 */
export const conversationSummaryListState = atom((get) => {
  const { isDescMode, isSbs } = get(sortConfigState);
  const summaryMap = get(conversationSummaryMapState);
  const summaryList = Array.from(summaryMap.values());

  const comparator = getSortComparator(isDescMode, isSbs);
  return summaryList.sort(comparator);
});

export const useConversationSummaryList = () => {
  return useAtomValue(conversationSummaryListState);
};

/**
 * 需要进行同步会话的平台
 */
export const needSyncTalkChannel: Conversation.Conversation['channel'][] = ['SHOPEE', 'MERCADO'];

const followedStoreListState = atom<StoreInfo[]>([]);

export const useFollowedStoreListState = () => {
  return useAtomValue(followedStoreListState);
};

export const useSetFollowedStoreListState = () => {
  return useSetAtom(followedStoreListState);
};

const reviewFollowedStoreListState = atom<StoreInfo[]>([]);

export const useReviewFollowedStoreListState = () => {
  return useAtomValue(reviewFollowedStoreListState);
};

export const useSetReviewFollowedStoreListState = () => {
  return useSetAtom(reviewFollowedStoreListState);
};

/**
 * 已排序且过滤的会话摘要数组
 * 基于 conversationSummaryListState，不受 LRU 回收影响
 * 用于会话列表渲染
 */
export const conversationListFilterState = atom((get) => {
  const conversationCategory = get(conversationCategoryState);
  const categoryFilterState = get(categoryFilterStateFamily(conversationCategory));

  // 使用 summaryList 替代 conversationList，不受 LRU 回收影响
  const summaryList = get(conversationSummaryListState);
  const currentConversationId = get(currentConversationIdState);
  const followedStores = get(followedStoreListState);
  const convIdList = get(conversationIdListState);
  const orgConfig = get(orgConfigState);
  const { importantTodoTypes } = get(importantTodoState) || {};

  const storeList = categoryFilterState.storeFilter?.storeList ?? [];
  const storePlatform = categoryFilterState.storeFilter?.platform;
  const storeRegion = categoryFilterState.storeFilter?.region;
  const storeFollower = categoryFilterState.storeFilter?.follower;

  // 预计算 Set 和 Map 以提升查找效率 O(n) -> O(1)
  const convIdSet = new Set(convIdList);
  const followedTunnelIdSet = new Set(followedStores.map((x) => x.tunnelAccountId));
  const selectedStoreIdSet = storeList?.length
    ? new Set(storeList.map((x) => x.tunnelAccountId))
    : null;

  const conversationFilter = categoryFilterState.filterAdvance;

  const {
    statusFilters,
    asteriskFilters,
    assistantFilters,
    salesStatusFilters,
    onlyShopeeAffiliate,
    excludeShopeeAffiliate,
    existKnowledgePopup,
  } = conversationFilter ?? {};

  // 预计算状态过滤条件
  const hasImportantTodo = statusFilters?.includes('IMPORTANT_TODO');
  const hasUnread = statusFilters?.includes('UNREAD');
  const hasUnprocessed = statusFilters?.includes('UNPROCESSED');
  const hasBotReplied = statusFilters?.includes('BOT_REPLIED');
  const hasNotClose = statusFilters?.includes('NOT_CLOSE');
  const hasWaitAssign = statusFilters?.includes('WAIT_ASSIGN');
  const hasInReception = statusFilters?.includes('IN_RECEPTION');
  const hasManualClose = statusFilters?.includes('MANUAL_CLOSE');
  const isSimplus = orgConfig.whiteListConfig?.SIMPLUS_CONV_STATUS;

  // 单次遍历，应用所有过滤条件
  return summaryList.filter((conv) => {
    const { conversationId } = conv;
    const isCurrentConv = conversationId === currentConversationId;

    // 当前会话始终保留
    if (isCurrentConv) return true;

    // 基础过滤：必须在 ID 列表中且有效
    if (!convIdSet.has(conversationId)) return false;
    if (conv.mockExternal || !conv.talkId || conv.talkId === '0') return false;

    // 重要待办过滤
    if (hasImportantTodo && !importantTodoTypes?.includes(conv.todoType)) {
      return false;
    }

    // 星标过滤
    if (asteriskFilters?.length && !asteriskFilters.includes(conv.colorAsterisk)) {
      return false;
    }

    // 客服过滤
    if (assistantFilters?.length && !assistantFilters.includes(conv.lastAssignedId)) {
      return false;
    }

    // 销售状态过滤
    if (salesStatusFilters?.length && !salesStatusFilters.includes(conv.salesStatus)) {
      return false;
    }

    // 会话状态过滤（互斥条件）
    if (hasUnread) {
      if (conv.unreadCount <= 0) return false;
    } else if (hasUnprocessed && hasBotReplied) {
      const isUnprocessed = !conv.isAffiliateSip && !conv.processed;
      const isBotRepliedMatch = conv.botMark;
      if (!isUnprocessed && !isBotRepliedMatch) return false;
    } else if (hasUnprocessed && hasNotClose) {
      const isUnprocessed = !conv.isAffiliateSip && !conv.processed;
      if (!isUnprocessed && !conv.isSplsNotClose) return false;
    } else if (hasUnprocessed) {
      if (conv.isAffiliateSip || conv.processed) return false;
    } else if (hasBotReplied) {
      if (isSimplus) {
        if (!conv.isSplsBotReplied) return false;
      } else {
        if (!conv.botMark) return false;
      }
    } else if (hasWaitAssign) {
      if (!conv.isWaitAssign) return false;
    } else if (hasInReception) {
      if (!conv.inReception) return false;
    } else if (hasManualClose) {
      if (isSimplus) {
        if (!conv.isSplsManualClose) return false;
      } else {
        if (conv.inReception || !conv.processed) return false;
      }
    } else if (hasNotClose) {
      if (!conv.isSplsNotClose) return false;
    }

    // 店铺过滤
    if (storePlatform && conv.channel !== storePlatform) {
      return false;
    }
    if (storeRegion && conv.region !== storeRegion) {
      return false;
    }
    if (
      selectedStoreIdSet &&
      conv.tunnelAccountId &&
      !selectedStoreIdSet.has(conv.tunnelAccountId)
    ) {
      return false;
    }
    if (storeFollower && conv.tunnelAccountId && !followedTunnelIdSet.has(conv.tunnelAccountId)) {
      return false;
    }

    // Shopee Affiliate 过滤
    if (onlyShopeeAffiliate) {
      if (!conv.isSellerAffiliateChat || conv.channel !== 'SHOPEE') return false;
    }
    if (excludeShopeeAffiliate) {
      if (conv.isSellerAffiliateChat || conv.channel !== 'SHOPEE') return false;
    }

    // 知识弹窗过滤
    if (existKnowledgePopup && !conv.existKnowledgePopup) {
      return false;
    }

    return true;
  });
});

/** 外部获取已排序且过滤的`当前应用已获取的全部会话信息`数组, 按最新消息降序排列 */
export const useConversationListFilter = () => {
  return useAtomValue(conversationListFilterState);
};

// #region 细粒度订阅 - 避免不必要的重渲染

/**
 * 只订阅过滤后会话列表的长度
 * 适用于只需要显示数量的场景（如徽章、计数器）
 */
export const conversationListLengthState = selectAtom(
  conversationListFilterState,
  (list) => list.length,
);

export const useConversationListLength = () => {
  return useAtomValue(conversationListLengthState);
};

/**
 * 只订阅过滤后会话列表的 ID 数组
 * 适用于虚拟列表只需要知道 ID 顺序的场景
 */
export const conversationIdListFilteredState = selectAtom(
  conversationListFilterState,
  (list) => list.map((conv) => conv.conversationId),
  (a, b) => a.length === b.length && a.every((id, i) => id === b[i]),
);

export const useConversationIdListFiltered = () => {
  return useAtomValue(conversationIdListFilteredState);
};

/**
 * 创建单个会话的选择器 atom
 * 只在指定字段变化时触发更新，避免无关字段变化导致重渲染
 *
 * @param conversationId - 会话 ID
 * @param selector - 选择器函数，从会话中提取需要的字段
 * @param equalityFn - 可选的相等性比较函数
 */
export const createConversationSelector = <T>(
  conversationId: string,
  selector: (conv: Conversation | undefined) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  return selectAtom(conversationAtomFamily(conversationId), selector, equalityFn);
};

// #endregion

/**
 * 把会话或会话数组作用到`当前应用已获取的全部会话信息`.
 * @param type : set:新增或更新; del:删除
 */
export const useMakeConversationCache = (type: 'set' | 'del') => {
  const setConversation = useSetConversation();
  const deleteConversation = useDeleteConversation();
  const getConversationById = useGetConversationById();
  const batchUpdateConversations = useBatchUpdateConversations();

  return useMemoizedFn(
    (conversationOrList: Conversation.Conversation | Conversation.Conversation[]) => {
      const conversationList = Array.isArray(conversationOrList)
        ? conversationOrList
        : [conversationOrList];

      if (type === 'del') {
        deleteConversation(conversationList.map((c) => c.conversationId));
        return;
      }

      // 批量处理 set 操作
      const processedConversations = conversationList.map((conversation) => {
        const { conversationId: id } = conversation;
        const preConversation = getConversationById(id);

        let lastMsg = preConversation;
        if (
          !lastMsg ||
          !preConversation?.conversationId ||
          (preConversation &&
            (parseInt(conversation.lastMsgTime, 10) >= parseInt(preConversation.lastMsgTime, 10) ||
              Number(conversation.lastMsgId) >= Number(preConversation.lastMsgId)))
        ) {
          lastMsg = conversation;
        }

        const todoInfo =
          preConversation &&
          (Number(preConversation.todoId) || 0) > (Number(conversation.todoId) || 0)
            ? {
                todoId: preConversation.todoId,
                todoType: preConversation.todoType,
                todoTypeName: preConversation.todoTypeName,
              }
            : {
                todoId: conversation.todoId,
                todoType: conversation.todoType,
                todoTypeName: conversation.todoTypeName,
              };

        return {
          ...preConversation,
          ...conversation,
          ...todoInfo,
          lastMsgContent: lastMsg.lastMsgContent,
          lastMsgId: lastMsg.lastMsgId,
          lastMsgTime: lastMsg.lastMsgTime,
          lastMsgFromType: lastMsg.lastMsgFromType,
          failCause: lastMsg.failCause,
        } as Conversation.Conversation;
      });

      if (processedConversations.length === 1) {
        setConversation(processedConversations[0].conversationId, processedConversations[0]);
      } else {
        batchUpdateConversations(processedConversations);
      }
    },
  );
};

// #endregion

// #region 接待中的单个会话信息

/** 接待中的单个会话信息 */
export const currentConversationState = atom((get) => {
  const currentConversationId = get(currentConversationIdState);
  if (currentConversationId === null) {
    return undefined;
  }
  // 使用 atomFamily 获取单个会话，实现细粒度更新
  return get(conversationAtomFamily(currentConversationId));
});

/** 外部获取`接待中的单个会话信息` */
export const useCurrentConversation = () => {
  return useAtomValue(currentConversationState);
};

/** 外部获取`当前操作的会话数据` */
export function useGetConversationInfo(batchFetch?: boolean) {
  const setConversation = useSetConversation();
  const getConversationById = useGetConversationById();

  return useCallback(
    async (conversationId: string) => {
      const conversation = getConversationById(conversationId);

      if (!conversation) {
        const conv = batchFetch
          ? await batchGetTalk(conversationId)
          : await getTalkInfoWithPhone({ conversationId, needPhone: true });
        setConversation(conv.conversationId, conv);
        return conv;
      }
      return conversation;
    },
    [setConversation, getConversationById, batchFetch],
  );
}

/**
 *  批量预获取会话信息
 */
export function usePrefetchConversations() {
  const batchUpdateConversations = useBatchUpdateConversations();
  return useMemoizedFn(async (conversationIds: string[]) => {
    if (conversationIds.length === 0) return;
    const uniqConversationIds = uniq(conversationIds);
    try {
      const res = await getTalkInfoListWithPhone(uniqConversationIds);
      const conversations = res?.aggTalks?.map(({ talk }) => talk);
      batchUpdateConversations(conversations);
      return conversations;
    } catch (error: any) {
      logger.error(`[usePrefetchConversations] Failed to fetch conversations: ${error?.message}`);
    }
  });
}

// #endregion

export type LocalStatus =
  | 'uploading'
  | 'loading'
  | 'processing'
  | 'synced'
  | 'error'
  | 'ack_timeout';

export interface LocalMessage extends Message {
  /**
   * 本地寄存状态
   * - uploading: message resource uploading
   * - loading: waiting send response
   * - processing: waiting for ack
   * - synced: did ack
   * - error: any error
   */
  localStatus?: LocalStatus;
  /**
   * 图片预览地址
   */
  preview?: string;
  /**
   * tag: 聊天窗口上传的消息，图片可以使用本地或者缓存
   */
  localUpload?: boolean;
  // 消息发送失败以后的内容，后端是直接扔到content里面的（textContent/imageContent)，本地发送失败以后需要做一大堆格式转换，这里做个处理统一放到localMessage里面。
  failCause?: Conversation.FailContent;

  // 发送失败以后可以重新发送
  resendContext?: {
    doResend: (content: LocalMessage) => Promise<boolean | LocalMessage>;
    content: LocalMessage;
  };

  hideTranslate?: boolean;
}

export const unreadMessageCountState = atom<number>(0);

const getUsedStoreIdMap = (storeIdList: string[]) => {
  return storeIdList.reduce<Partial<Record<string, true>>>((res, storeId) => {
    res[storeId] = true;

    return res;
  }, {});
};

const tradeMapAtom = atom<Record<Conversation.Trade.Vo['internalTradeId'], Conversation.Trade.Vo>>(
  {},
);
export const tradeMapState = atom(
  (get) => get(tradeMapAtom),
  (_, set, next: Record<Conversation.Trade.Vo['internalTradeId'], Conversation.Trade.Vo>) => {
    const usedStoreIdMap = getUsedStoreIdMap(lastUsedExternalStoreIdCache.data);

    const filteredNext = Object.values(next).reduce<typeof next>((res, cur) => {
      const { internalTradeId, externalShopId } = cur;

      const { [externalShopId]: isStoreUsed = false } = usedStoreIdMap;

      if (isStoreUsed) {
        res[internalTradeId] = cur;
      }

      return res;
    }, {});

    set(tradeMapAtom, filteredNext);
  },
);

/**
 * 以extenalItemId作为索引
 */
export const tradeExtenalMapState = atom((get) => {
  const internalMap = get(tradeMapState);
  return Object.values(internalMap).reduce((result, p) => {
    // eslint-disable-next-line no-param-reassign
    result[p.externalTradeId] = p;
    return result;
  }, {} as Record<Conversation.Trade.Vo['externalTradeId'], Conversation.Trade.Vo>);
});

const productMapAtom = atom<Record<Conversation.Product['internalItemId'], Conversation.Product>>(
  {},
);
export const productMapState = atom(
  (get) => get(productMapAtom),
  (_, set, next: Record<Conversation.Product['internalItemId'], Conversation.Product>) => {
    const usedStoreIdMap = getUsedStoreIdMap(lastUsedStoreIdCache.data);

    const filteredNext = Object.values(next).reduce<typeof next>((res, cur) => {
      const { internalItemId, internalStoreId } = cur;

      const { [internalStoreId]: isStoreUsed = false } = usedStoreIdMap;

      if (isStoreUsed) {
        res[internalItemId] = cur;
      }

      return res;
    }, {});

    set(productMapAtom, filteredNext);
  },
);

/**
 * 以extenalItemId作为索引
 */
export const productExtenalMapState = atom((get) => {
  const internalMap = get(productMapState);
  return Object.values(internalMap).reduce((result, p) => {
    // eslint-disable-next-line no-param-reassign
    result[p.externalItemId] = p;
    return result;
  }, {} as Record<Conversation.Product['externalItemId'], Conversation.Product>);
});

const voucherMapAtom = atom<
  Record<
    Conversation.Voucher.Vo['externalVoucherId'],
    Conversation.Voucher.Vo & { internalStoreId: string }
  >
>({});
export const voucherMapState = atom(
  (get) => get(voucherMapAtom),
  (
    _,
    set,
    next: Record<
      Conversation.Voucher.Vo['externalVoucherId'],
      Conversation.Voucher.Vo & { internalStoreId: string }
    >,
  ) => {
    const usedStoreIdMap = getUsedStoreIdMap(lastUsedStoreIdCache.data);

    const filteredNext = Object.values(next).reduce<typeof next>((res, cur) => {
      const { externalVoucherId, internalStoreId } = cur;

      const { [internalStoreId]: isStoreUsed = false } = usedStoreIdMap;

      if (isStoreUsed) {
        res[externalVoucherId] = cur;
      }

      return res;
    }, {});

    set(voucherMapAtom, filteredNext);
  },
);

export const useUnreadMessageCountValue = () => {
  return useAtomValue(unreadMessageCountState);
};

export const useSetUnreadMessageCountValue = () => {
  return useSetAtom(unreadMessageCountState);
};

export const useTradeMapValue = () => {
  return useAtomValue(tradeMapState);
};

export const useSetTradeMapValue = () => {
  return useSetAtom(tradeMapState);
};

export const useCurrentConversationValue = () => {
  return useAtomValue(currentConversationState);
};

export const useProductMapValue = () => {
  return useAtomValue(productMapState);
};

export const useSetProductMapValue = () => {
  return useSetAtom(productMapState);
};

export const useVoucherMapValue = () => {
  return useAtomValue(voucherMapState);
};

export const useSetVoucherMapValue = () => {
  return useSetAtom(voucherMapState);
};

export const useAddTradeToMap = () => {
  return useAtomCallback(
    useCallback((get, set, tradeOrList: Conversation.Trade.Vo | Conversation.Trade.Vo[]) => {
      const tradeList = Array.isArray(tradeOrList) ? tradeOrList : [tradeOrList];

      if (tradeList.length === 0) {
        return;
      }

      const [{ externalShopId }] = tradeList;

      lastUsedExternalStoreIdCache.push(externalShopId);
      const tradeMap = get(tradeMapState);
      set(
        tradeMapState,
        tradeList.reduce(
          (res, trade) => {
            const { internalTradeId: id } = trade;

            res[id] = trade;

            return res;
          },
          {
            ...tradeMap,
          },
        ),
      );
    }, []),
  );
};

export const useAddProductToMap = () => {
  return useAtomCallback(
    useCallback((get, set, productOrList: Conversation.Product | Conversation.Product[]) => {
      const productList = Array.isArray(productOrList) ? productOrList : [productOrList];

      if (productList.length === 0) {
        return;
      }

      const [{ internalStoreId }] = productList;

      lastUsedStoreIdCache.push(internalStoreId);
      const productMap = get(productMapState);
      set(
        productMapState,
        productList.reduce(
          (res, product) => {
            const { internalItemId: id } = product;

            res[id] = product;

            return res;
          },
          {
            ...productMap,
          },
        ),
      );
    }, []),
  );
};

export const useAddVoucherToMap = () => {
  const voucherMap = useAtomValue(voucherMapState);
  const setVoucherMap = useSetAtom(voucherMapState);
  return useCallback(
    (
      voucherOrList: Conversation.Voucher.Vo | Conversation.Voucher.Vo[],
      internalStoreId: string,
    ) => {
      const voucherList = Array.isArray(voucherOrList) ? voucherOrList : [voucherOrList];

      if (voucherList.length === 0) {
        return;
      }

      lastUsedStoreIdCache.push(internalStoreId);

      setVoucherMap(
        voucherList.reduce(
          (res, voucher) => {
            const { externalVoucherId: id } = voucher;

            res[id] = {
              ...voucher,
              internalStoreId,
            };

            return res;
          },
          {
            ...voucherMap,
          },
        ),
      );
    },
    [setVoucherMap, voucherMap],
  );
};

// 根据id删除
export const useDelConversationById = () => {
  const deleteConversation = useDeleteConversation();
  const setCurrentConversationId = useSetAtom(currentConversationIdState);
  const currentConversationId = useAtomValue(currentConversationIdState);

  return useCallback(
    async (id: string | string[]) => {
      const waitDelIds = Array.isArray(id) ? id : [id];

      // useDeleteConversation 会同时更新 Map 和 IdList
      deleteConversation(waitDelIds);

      if (currentConversationId && waitDelIds.includes(currentConversationId)) {
        setCurrentConversationId(null);
      }
    },
    [deleteConversation, currentConversationId, setCurrentConversationId],
  );
};

export const useGetConvIdByMsgId = () => {
  const getMessageById = useGetMessageById();
  return useCallback(
    async (msgId: number) => {
      return getMessageById(`${msgId}`)?.conversationId;
    },
    [getMessageById],
  );
};

export const getFailCauseMsg = (
  lang: General.SystemLanguage,
  failCause?: Conversation.FailContent,
) => {
  const { failMsg, englishFailMsg, failMsgMap } = failCause || {};

  const langMsgMap = {
    'zh-CN': failMsg,
    'en-US': englishFailMsg,
    ...failMsgMap,
  };

  const {
    'zh-CN': cnMsg = '',
    'en-US': fallbackMsg = cnMsg,
    [lang]: msg = fallbackMsg,
  } = langMsgMap;

  return msg;
};
